(() => {
  console.log("[store-stock] loaded");

  // -------------------------
  // Tiny DOM helpers
  // -------------------------
  const $$ = (root, sel) => Array.from((root || document).querySelectorAll(sel));
  const $ = (root, sel) => (root || document).querySelector(sel);

  const normalizeSku = (s) => (s || "").toString().trim().toUpperCase();

  // Toggle:
  // - true  => mock mode (safe for live)
  // - false => hit real endpoint
  const USE_MOCK = true;

  // When ready for Patchworks/app proxy:
  const ENDPOINT_URL = "/apps/lightspeed-availability"; // placeholder

  // Predictive search
  const SUGGEST_LIMIT = 8;
  const SUGGEST_DEBOUNCE_MS = 180;

  // -------------------------
  // UI helpers
  // -------------------------
  function escapeHtml(s) {
    return (s || "")
      .toString()
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  // UPDATED: hides meta line when empty (fixes duplicate “status line” noise)
  function setMeta(metaEl, msg, tone = "") {
    if (!metaEl) return;

    metaEl.classList.remove("is-ok", "is-bad", "is-warn", "is-loading");
    if (tone) metaEl.classList.add(tone);

    const text = (msg || "").toString().trim();
    metaEl.textContent = text;

    // Hide meta row entirely if there's nothing to say
    metaEl.hidden = !text;
  }

  function setResults(resultsEl, html) {
    if (!resultsEl) return;
    resultsEl.innerHTML = html || "";
  }

  function show(el) {
    if (!el) return;
    el.hidden = false;
  }

  function hide(el) {
    if (!el) return;
    el.hidden = true;
  }

  function debounce(fn, ms) {
    let t = null;
    return (...args) => {
      if (t) clearTimeout(t);
      t = setTimeout(() => fn(...args), ms);
    };
  }

  // -------------------------
  // Availability (mock + real)
  // -------------------------
  async function mockAvailability(storeCode, sku) {
    await new Promise((r) => setTimeout(r, 250));

    const demo = {
      "VOZ-ACE-MAX-BLU": {
        available_here: true,
        available_elsewhere: [],
      },
      "TENS-10ML-APPLE": {
        available_here: false,
        available_elsewhere: [
          { store_name: "Kings Heath", store_page_url: "/pages/kings-heath", store_code: "kings-heath" },
          { store_name: "Selly Oak", store_page_url: "/pages/selly-oak", store_code: "selly-oak" },
        ],
      },
    };

    const key = normalizeSku(sku);
    const hit = demo[key];

    if (!hit) {
      return {
        ok: true,
        sku: key,
        store_code: storeCode,
        updated: new Date().toISOString(),
        available_here: false,
        available_elsewhere: [],
      };
    }

    return {
      ok: true,
      sku: key,
      store_code: storeCode,
      updated: new Date().toISOString(),
      available_here: !!hit.available_here,
      available_elsewhere: Array.isArray(hit.available_elsewhere) ? hit.available_elsewhere : [],
    };
  }

  async function fetchAvailability(storeCode, outletId, sku) {
    if (USE_MOCK) return mockAvailability(storeCode, sku);

    const url = new URL(ENDPOINT_URL, window.location.origin);
    url.searchParams.set("store_code", storeCode || "");
    if (outletId) url.searchParams.set("outlet_id", outletId);
    url.searchParams.set("sku", sku || "");

    const res = await fetch(url.toString(), {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    if (!res.ok) throw new Error(`Availability lookup failed (${res.status})`);
    return res.json();
  }

  // UPDATED: no “meta” line for normal results (card already says it)
  function renderResponse(data) {
    const updated = data?.updated
      ? `<div class="store-stock__updated">Updated: ${escapeHtml(data.updated)}</div>`
      : "";

    if (!data?.ok) {
      const msg = data?.message || "Couldn’t check availability right now.";
      return {
        tone: "is-warn",
        meta: msg, // keep meta for warnings/errors
        html: `<div class="store-stock__card is-warn">${escapeHtml(msg)}${updated}</div>`,
      };
    }

    if (data.available_here) {
      return {
        tone: "is-ok",
        meta: "", // IMPORTANT: prevent duplicate status line
        html: `<div class="store-stock__card is-ok">
          <div class="store-stock__status">Available here ✅</div>
          ${updated}
        </div>`,
      };
    }

    const elsewhere = Array.isArray(data.available_elsewhere) ? data.available_elsewhere : [];
    if (!elsewhere.length) {
      return {
        tone: "is-bad",
        meta: "", // IMPORTANT: prevent duplicate status line
        html: `<div class="store-stock__card is-bad">
          <div class="store-stock__status">Not available here ❌</div>
          <div class="store-stock__sub">No other store availability to show yet.</div>
          ${updated}
        </div>`,
      };
    }

    const list = elsewhere
      .map((s) => {
        const name = escapeHtml(s.store_name || "Another store");
        const url = escapeHtml(s.store_page_url || "#");
        return `<li class="store-stock__li">
          <a class="store-stock__storelink" href="${url}">
            <span class="store-stock__storename">${name}</span>
            <span class="store-stock__storecta">View store</span>
          </a>
        </li>`;
      })
      .join("");

    return {
      tone: "is-bad",
      meta: "", // IMPORTANT: prevent duplicate status line
      html: `<div class="store-stock__card is-bad">
        <div class="store-stock__status">Not available here ❌</div>
        <div class="store-stock__sub">But it is available at:</div>
        <ul class="store-stock__list">${list}</ul>
        ${updated}
      </div>`,
    };
  }

  // -------------------------
  // Shopify search + product fetch
  // -------------------------
  async function fetchSuggestProducts(query) {
    const q = (query || "").trim();
    if (!q) return [];

    const url = new URL("/search/suggest.json", window.location.origin);
    url.searchParams.set("q", q);
    url.searchParams.set("resources[type]", "product");
    url.searchParams.set("resources[limit]", String(SUGGEST_LIMIT));
    url.searchParams.set("resources[options][unavailable_products]", "last");
    url.searchParams.set("resources[options][fields]", "title,product_type,vendor,variants.sku");

    const res = await fetch(url.toString(), { headers: { Accept: "application/json" } });
    if (!res.ok) throw new Error(`Suggest failed (${res.status})`);

    const data = await res.json();
    const products = data?.resources?.results?.products || [];

    return products.map((p) => ({
      id: p.id,
      title: p.title,
      handle: p.handle,
      url: p.url,
      image: p.featured_image?.url || p.featured_image || "",
    }));
  }

  async function fetchProductJS(handle) {
    const url = `/products/${encodeURIComponent(handle)}.js`;
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    if (!res.ok) throw new Error(`Product fetch failed (${res.status})`);
    return res.json();
  }

  // -------------------------
  // Suggestions rendering
  // -------------------------
  function renderSuggestItems(items) {
    if (!items.length) return "";
    return items
      .map((p) => {
        const title = escapeHtml(p.title || "Product");
        const handle = escapeHtml(p.handle || "");
        const img = p.image ? `<img loading="lazy" alt="" src="${escapeHtml(p.image)}">` : "";
        return `
          <button type="button" class="store-suggest__item" data-handle="${handle}">
            <span class="store-suggest__thumb">${img}</span>
            <span class="store-suggest__title">${title}</span>
          </button>
        `;
      })
      .join("");
  }

  function clearSuggest(suggestEl) {
    if (!suggestEl) return;
    suggestEl.innerHTML = "";
    hide(suggestEl);
  }

  // -------------------------
  // Variant dropdown
  // -------------------------
  function fillVariantSelect(selectEl, variants) {
    if (!selectEl) return;
    selectEl.innerHTML = "";
    variants.forEach((v, idx) => {
      const label = v.public_label || v.title || `Option ${idx + 1}`;
      const opt = document.createElement("option");
      opt.value = v.sku || "";
      opt.textContent = label;
      selectEl.appendChild(opt);
    });
  }

  function buildVariantListFromProduct(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    return variants
      .map((v) => ({
        id: v.id,
        title: v.title,
        sku: normalizeSku(v.sku),
        available: !!v.available,
        public_label:
          v.title && v.title !== "Default Title" ? v.title : normalizeSku(v.sku) || "Option",
      }))
      .filter((v) => v.sku);
  }

  // -------------------------
  // Floating panel anchoring (fixes “beanstalk”)
  //   - Suggestions float (anchored)
  //   - Variant picker stays INLINE (no floating)
  // -------------------------
  function ensureFloatingBase(widget) {
    const st = window.getComputedStyle(widget);
    if (st.position === "static") widget.style.position = "relative";
  }

  function positionPanelToInput(widget, inputEl, panelEl, { offsetY = 10, matchWidth = true } = {}) {
    if (!widget || !inputEl || !panelEl) return;

    ensureFloatingBase(widget);

    const wRect = widget.getBoundingClientRect();
    const iRect = inputEl.getBoundingClientRect();

    const left = Math.max(0, iRect.left - wRect.left);
    const top = Math.max(0, iRect.bottom - wRect.top + offsetY);
    const width = matchWidth ? Math.max(200, iRect.width) : panelEl.offsetWidth;

    panelEl.style.position = "absolute";
    panelEl.style.left = `${left}px`;
    panelEl.style.top = `${top}px`;
    panelEl.style.width = `${width}px`;
    panelEl.style.zIndex = "50";
  }

  function styleSuggestPanel(panelEl) {
    if (!panelEl) return;
    panelEl.style.maxHeight = "340px";
    panelEl.style.overflow = "auto";
    panelEl.style.borderRadius = "18px";
    panelEl.style.background = "rgba(0,0,0,.86)";
    panelEl.style.border = "1px solid rgba(255,255,255,.14)";
    panelEl.style.backdropFilter = "blur(10px)";
    panelEl.style.webkitBackdropFilter = "blur(10px)";
    panelEl.style.boxShadow = "0 18px 50px rgba(0,0,0,.45)";
  }

  // INLINE variant wrap styling (no floating!)
  function styleVariantWrap(panelEl) {
    if (!panelEl) return;

    panelEl.style.marginTop = "12px";
    panelEl.style.display = "block";
    panelEl.style.borderRadius = "18px";
    panelEl.style.background = "rgba(255,255,255,.06)";
    panelEl.style.border = "1px solid rgba(255,255,255,.14)";
    panelEl.style.padding = "10px 14px";
    panelEl.style.backdropFilter = "blur(10px)";
    panelEl.style.webkitBackdropFilter = "blur(10px)";

    // important: do NOT float
    panelEl.style.position = "relative";
    panelEl.style.left = "";
    panelEl.style.top = "";
    panelEl.style.width = "";
    panelEl.style.zIndex = "5";
  }

  // -------------------------
  // Wire a widget
  // -------------------------
  function wireWidget(widget) {
    if (!widget || widget.dataset.storeStockWired === "1") return;
    widget.dataset.storeStockWired = "1";

    // Respect kiosk suggest toggle
    const suggestEnabledAttr = widget.getAttribute("data-suggest-enabled");
    const SUGGEST_ENABLED = suggestEnabledAttr !== "0";

    const storeCode = widget.getAttribute("data-store-code") || "";
    const outletId = widget.getAttribute("data-outlet-id") || "";

    const form = $(widget, "[data-store-stock-form]");
    const queryInput = $(widget, "[data-store-stock-query]") || $(widget, "[data-store-stock-sku]");
    const metaEl = $(widget, "[data-store-stock-meta]");
    const resultsEl = $(widget, "[data-store-stock-results]");

    const suggestEl = $(widget, "[data-store-stock-suggest]");
    const variantWrap = $(widget, "[data-store-stock-variant]");
    const variantSelect = $(widget, "[data-store-stock-variant-select]");

    if (!form || !queryInput) {
      console.warn("[store-stock] missing form or input", { form: !!form, queryInput: !!queryInput, widget });
      return;
    }

    // Style panels
    if (suggestEl) styleSuggestPanel(suggestEl);
    if (variantWrap) styleVariantWrap(variantWrap);

    // IMPORTANT: prevent hidden suggest panel stealing clicks
    if (suggestEl) suggestEl.style.pointerEvents = "none";

    // Style panels
    if (suggestEl) styleSuggestPanel(suggestEl);
    if (variantWrap) styleVariantWrap(variantWrap);

    // HARD RESET: variant picker must start hidden + empty (prevents “ghost selector”)
    if (variantWrap) {
    variantWrap.hidden = true;
    variantWrap.setAttribute("data-variant-ready", "0");
    }
    if (variantSelect) {
    variantSelect.innerHTML = "";
    }
    
    // Internal state
    let chosenSku = "";
    let lastSuggestQuery = "";
    let suggestOpen = false;

    function setChosenSku(sku) {
      chosenSku = normalizeSku(sku);
    }

    function openSuggest() {
      if (!suggestEl) return;
      positionPanelToInput(widget, queryInput, suggestEl, { offsetY: 10, matchWidth: true });
      suggestEl.style.pointerEvents = "auto";
      show(suggestEl);
      suggestOpen = true;
    }

    function closeSuggest() {
      if (!suggestEl) return;
      clearSuggest(suggestEl);
      suggestEl.style.pointerEvents = "none";
      suggestOpen = false;
    }

    function showVariantPicker(variants) {
      if (!variantWrap || !variantSelect) return;
      fillVariantSelect(variantSelect, variants);

      variantWrap.hidden = false;
      variantWrap.setAttribute("data-variant-ready", "1");

      setChosenSku(variantSelect.value);
    }

    function hideVariantPicker() {
      if (!variantWrap || !variantSelect) return;
      variantWrap.hidden = true;
      variantWrap.setAttribute("data-variant-ready", "0");
      variantSelect.innerHTML = "";
    }

    // Keep floating suggest aligned (ONLY suggest floats)
    const reposition = () => {
      if (suggestEl && !suggestEl.hidden) {
        positionPanelToInput(widget, queryInput, suggestEl, { offsetY: 10, matchWidth: true });
      }
    };
    window.addEventListener("resize", reposition, { passive: true });
    window.addEventListener("scroll", reposition, { passive: true });

    // Debounced suggest fetch
    const runSuggest = debounce(async () => {
      if (!SUGGEST_ENABLED) return;
      if (!suggestEl) return;

      const q = (queryInput.value || "").trim();
      lastSuggestQuery = q;

      if (!q || q.length < 2) {
        closeSuggest();
        return;
      }

      try {
        const products = await fetchSuggestProducts(q);

        // Ignore stale responses
        if ((queryInput.value || "").trim() !== lastSuggestQuery) return;

        if (!products.length) {
          closeSuggest();
          return;
        }

        suggestEl.innerHTML = renderSuggestItems(products);
        openSuggest();
      } catch (e) {
        closeSuggest();
        console.warn("[store-stock] suggest error", e);
      }
    }, SUGGEST_DEBOUNCE_MS);

    // Outside click closer (bubble phase so it doesn't steal clicks)
    const onDocClick = (ev) => {
      if (!suggestEl || !suggestOpen) return;
      if (widget.contains(ev.target)) return;
      closeSuggest();
    };
    document.addEventListener("click", onDocClick, false);

    // Best-effort cleanup
    widget.addEventListener("shopify:section:unload", () => {
      document.removeEventListener("click", onDocClick, false);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition);
    });

    // Suggest click -> load product -> show variants if needed
    if (suggestEl) {
      suggestEl.addEventListener("click", async (e) => {
        const btn = e.target?.closest?.("[data-handle]");
        if (!btn) return;

        const handle = btn.getAttribute("data-handle") || "";
        if (!handle) return;

        closeSuggest();
        hideVariantPicker();

        setMeta(metaEl, "Loading options…", "is-loading");
        setResults(resultsEl, "");

        try {
          const product = await fetchProductJS(handle);
          const variants = buildVariantListFromProduct(product);

          if (!variants.length) {
            setMeta(metaEl, "No SKUs found for this product.", "is-warn");
            setChosenSku("");
            return;
          }

          queryInput.value = product.title || queryInput.value;

          if (variants.length === 1) {
            setChosenSku(variants[0].sku);
            setMeta(metaEl, "Ready to check availability.", "");
            return;
          }

          showVariantPicker(variants);
          setMeta(metaEl, "Choose an option, then Check.", "");
        } catch (err) {
          setMeta(metaEl, "Couldn’t load product options.", "is-warn");
          setResults(resultsEl, "");
          console.warn("[store-stock] product fetch error", err);
        }
      });
    }

    if (variantSelect) {
      variantSelect.addEventListener("change", () => {
        setChosenSku(variantSelect.value);
      });
    }

    // Input typing
    queryInput.addEventListener("input", () => {
      const raw = (queryInput.value || "").trim();

      // new search: clear old UI
      setResults(resultsEl, "");
      setMeta(metaEl, "", "");
      hideVariantPicker();

      // Heuristic: likely SKU if no spaces and has alnum and >= 4 chars
      const looksSku = raw.length >= 4 && !/\s/.test(raw) && /[A-Za-z0-9]/.test(raw);

      if (looksSku) {
        setChosenSku(raw);
        closeSuggest();
        return;
      }

      setChosenSku("");
      runSuggest();
    });

    queryInput.addEventListener("focus", () => {
      if (!SUGGEST_ENABLED) return;
      if (suggestEl && suggestEl.innerHTML.trim()) openSuggest();
    });

    queryInput.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        closeSuggest();
        hideVariantPicker();
      }
    });

    // Submit
    form.addEventListener("submit", async (e) => {
      e.preventDefault();

      const typed = normalizeSku(queryInput.value);
      const sku = chosenSku || typed;

      if (!sku) {
        setMeta(metaEl, "Search and select a product (or type a SKU).", "is-warn");
        setResults(resultsEl, "");
        queryInput.focus();
        return;
      }

      setMeta(metaEl, "Checking…", "is-loading");
      setResults(resultsEl, "");
      closeSuggest();

      try {
        const data = await fetchAvailability(storeCode, outletId, sku);
        const view = renderResponse(data);
        setMeta(metaEl, view.meta, view.tone);
        setResults(resultsEl, view.html);
      } catch (err) {
        setMeta(metaEl, "Couldn’t check right now. Please try again.", "is-warn");
        setResults(resultsEl, "");
        console.warn("[store-stock]", err);
      }
    });
  }

  function init() {
    $$(document, "[data-store-stock]").forEach(wireWidget);
  }

  document.addEventListener("shopify:section:load", init);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
